import type { Session } from '@supabase/supabase-js';

const ACCESS_COOKIE_NAMES = ['sb-access-token', 'supabase-access-token'] as const;
const REFRESH_COOKIE_NAMES = ['sb-refresh-token', 'supabase-refresh-token'] as const;

let cachedAccessToken: string | null = null;

const getBaseAttributes = (): string => {
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  return `; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`;
};

const writeCookie = (name: string, value: string, maxAgeSeconds?: number): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const maxAge =
    typeof maxAgeSeconds === 'number' && Number.isFinite(maxAgeSeconds)
      ? `; Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`
      : '';

  document.cookie = `${name}=${encodeURIComponent(value)}${maxAge}${getBaseAttributes()}`;
};

const clearCookie = (name: string): void => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=; Max-Age=0${getBaseAttributes()}`;
};

export const syncSupabaseSessionCookies = (session: Session | null): void => {
  cachedAccessToken = session?.access_token ?? null;

  if (!session || !session.access_token) {
    [...ACCESS_COOKIE_NAMES, ...REFRESH_COOKIE_NAMES].forEach(clearCookie);
    return;
  }

  const maxAge = typeof session.expires_in === 'number' ? session.expires_in : undefined;
  ACCESS_COOKIE_NAMES.forEach((name) => writeCookie(name, session.access_token, maxAge));

  const refreshToken =
    typeof session.refresh_token === 'string' && session.refresh_token.length > 0
      ? session.refresh_token
      : null;

  if (refreshToken) {
    REFRESH_COOKIE_NAMES.forEach((name) => writeCookie(name, refreshToken, maxAge));
  } else {
    REFRESH_COOKIE_NAMES.forEach(clearCookie);
  }
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const pattern = new RegExp(`(?:^|; )${name.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')}=([^;]*)`);
  const match = document.cookie.match(pattern);
  if (!match || match.length < 2) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const getSupabaseAccessTokenSnapshot = (): string | null => {
  if (cachedAccessToken) {
    return cachedAccessToken;
  }

  for (const name of ACCESS_COOKIE_NAMES) {
    const token = readCookie(name);
    if (token) {
      cachedAccessToken = token;
      return token;
    }
  }

  return null;
};

